/* Eén Site lezen, voor /crm/sites/:siteId (F9.12).
 *
 * Een Site (public.sites) is een opgeleverde installatie in beheer, het object
 * van nazorg en monitoring (CONTEXT.md). Deze pagina toont de site zelf en
 * daaronder het EMS (src/features/ems/crm/ui/EmsSectie.tsx).
 *
 * "Bestaat niet", "geen toegang" en "fout" zijn drie dingen. RLS weigert een
 * SELECT niet maar filtert hem, dus nul rijen moet geduid worden. Gemeten
 * 13-09-2026 op fxlorumfwzrdwiftbalo: de policy users_view_sites is SELECT
 * `true` voor `authenticated`, en anon heeft wel een grant maar geen policy.
 * Nul rijen mét een geldige sessie betekent dus echt "bestaat niet"; zonder
 * sessie is het "geen toegang".
 *
 * Relatieve imports (geen @/), zodat de toetsen dit bestand zonder bundel lezen. */

import { classificeer } from '../../ems/crm/data/leesEms';

export type SiteFoutSoort = 'geen-toegang' | 'niet-gevonden' | 'fout';

export class SiteFout extends Error {
  soort: SiteFoutSoort;
  verwijderdOp: string | null;
  constructor(soort: SiteFoutSoort, bericht: string, verwijderdOp: string | null = null) {
    super(bericht);
    this.name = 'SiteFout';
    this.soort = soort;
    this.verwijderdOp = verwijderdOp;
  }
}

/** Het deel van de supabase-js-client dat hier gebruikt wordt. */
export interface SiteClient {
  from(tabel: string): any;
  auth: { getSession(): PromiseLike<{ data: { session: { user: { id: string } } | null }; error: any }> };
}

export interface SiteProject {
  id: string;
  /** ok: gelezen; niet-gevonden: de site verwijst naar een project dat er niet (meer) is (er is geen FK); fout: niet te lezen */
  staat: 'ok' | 'niet-gevonden' | 'verwijderd' | 'fout';
  naam: string | null;
  nummer: string | null;
  melding: string | null;
}

export interface Site {
  id: string;
  naam: string;
  adres: string | null;
  latitude: number | null;
  longitude: number | null;
  type: string | null;
  status: string | null;
  nummer: string | null;
  beschrijving: string | null;
  /** Link naar de SharePoint-map van de locatie, als die er is. */
  sharepoint: string | null;
  /** Wanneer de locatie in het CRM kwam. */
  aangemaakt: string | null;
  project: SiteProject | null;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const SITE_KOLOMMEN = 'id, name, address, latitude, longitude, site_type, status, site_number, project_id, description, sharepoint_main_url, created_at, deleted_at';

function alsSiteFout(e: { soort: string; message: string }): SiteFout {
  const soort: SiteFoutSoort = e.soort === 'geen-toegang' || e.soort === 'niet-gevonden' ? e.soort : 'fout';
  return new SiteFout(soort, e.message);
}

const getal = (v: unknown) => (v === null || v === undefined || !Number.isFinite(Number(v)) ? null : Number(v));

const datum = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' });
};

async function haalProject(client: SiteClient, projectId: string): Promise<SiteProject> {
  const leeg = { id: projectId, naam: null, nummer: null, melding: null };
  try {
    const { data, error, status } = await client.from('projects').select('id, name, project_number, deleted_at').eq('id', projectId);
    if (error) return { ...leeg, staat: 'fout', melding: classificeer(error, status, 'public.projects').message };
    const p = (data ?? [])[0];
    if (!p) return { ...leeg, staat: 'niet-gevonden' };
    return { id: projectId, staat: p.deleted_at ? 'verwijderd' : 'ok', naam: p.name ?? null, nummer: p.project_number ?? null, melding: null };
  } catch (e) {
    return { ...leeg, staat: 'fout', melding: e instanceof Error ? e.message : String(e) };
  }
}

export async function haalSite(client: SiteClient, siteId: string): Promise<Site> {
  if (typeof siteId !== 'string' || !UUID.test(siteId)) {
    throw new SiteFout('niet-gevonden', `"${String(siteId)}" is geen geldige site-id.`);
  }
  const { data, error, status } = await client.from('sites').select(SITE_KOLOMMEN).eq('id', siteId);
  if (error) throw alsSiteFout(classificeer(error, status, 'public.sites'));

  const rij = (data ?? [])[0];
  if (!rij) {
    const sessie = await client.auth.getSession();
    if (sessie.error) throw alsSiteFout(classificeer(sessie.error, undefined, 'sessie'));
    if (!sessie.data?.session?.user?.id) {
      throw new SiteFout('geen-toegang', 'Er is geen geldige sessie. Zonder sessie geeft public.sites geen enkele rij, dus is niet te zeggen of deze locatie bestaat.');
    }
    throw new SiteFout('niet-gevonden', `Er is geen locatie met id ${siteId} in public.sites.`);
  }
  if (rij.deleted_at) {
    throw new SiteFout('niet-gevonden', `Locatie "${rij.name}" is verwijderd op ${datum(rij.deleted_at)}.`, rij.deleted_at);
  }

  return {
    id: String(rij.id),
    naam: rij.name,
    adres: rij.address ?? null,
    latitude: getal(rij.latitude),
    longitude: getal(rij.longitude),
    type: rij.site_type ?? null,
    status: rij.status ?? null,
    nummer: rij.site_number ?? null,
    beschrijving: rij.description || null,
    sharepoint: rij.sharepoint_main_url || null,
    aangemaakt: rij.created_at ?? null,
    project: rij.project_id ? await haalProject(client, String(rij.project_id)) : null,
  };
}

export type SiteStaat =
  | { soort: 'laden' }
  | { soort: 'geen-toegang'; fout: SiteFout }
  | { soort: 'niet-gevonden'; fout: SiteFout }
  | { soort: 'fout'; fout: unknown }
  | { soort: 'site'; site: Site };

/** Wat de sitepagina tekent, uit de stand van de query. */
export function siteStaatVoor(q: { isPending: boolean; isError: boolean; error?: unknown; data?: Site }): SiteStaat {
  if (q.isPending) return { soort: 'laden' };
  if (q.isError) {
    if (q.error instanceof SiteFout && q.error.soort === 'geen-toegang') return { soort: 'geen-toegang', fout: q.error };
    if (q.error instanceof SiteFout && q.error.soort === 'niet-gevonden') return { soort: 'niet-gevonden', fout: q.error };
    return { soort: 'fout', fout: q.error };
  }
  return { soort: 'site', site: q.data as Site };
}
